export enum SenderType {
  PATIENT = "patient",
  CLINICIAN = "clinician",
  SYSTEM = "system",
}

export enum MessageType {
  TEXT = "text",
  VIDEO_CALL = "video_call",
}

export interface BaseMessage {
  id: string;
  senderId: string;
  body: {
    text: string;
    markdown?: boolean;
  };
  createdAt: string;
}

export interface HumanMessage extends BaseMessage {
  senderType: SenderType.PATIENT | SenderType.CLINICIAN;
}

export interface SystemMessage extends BaseMessage {
  senderType: SenderType.SYSTEM;
}

export type ChatMessage = HumanMessage | SystemMessage;

/**
 * Message type enum matching the Python schema.
 * These values determine the kind of payload required in the message `body` field.
 */
export enum UniversalMessageType {
  TEXT = 2,
  ATTACHMENT = 10,
  SURVEY = 73,
  RESOURCE = 1001,
}

/**
 * Absence of this field means the message is visible to all participants.
 */
export enum MessageVisibilityRestriction {
  PROFESSIONAL_ONLY = "professional-only",
  PATIENT_ONLY = "patient-only",
}

export enum MessageProcessingStatus {
  UNPROCESSED = "UNPROCESSED",
  PROCESSED = "PROCESSED",
  FAILED = "FAILED",
}

export interface MessageBodyText {
  text: string;
  replySuggestionType?: string | null;
  replySuggestions?: string[] | null;
  markdown?: boolean;
  forceSuggestedReply?: boolean;
}

export interface MessageBodyAttachment {
  url: string;
  contentType: string;
  filename?: string | null;
  text?: string | null;
  markdown?: boolean;
  documentReferenceId?: string | null;
}

export interface MessageBodySurvey {
  triggerEvent: string;
  final: boolean;
}

export interface MessageBodyResource {
  resourceId: string;
  resourceType: string;
  resourceVersion: string;
  text: string;
  markdown?: boolean;
}

export type UniversalChatMessageBody =
  | MessageBodyText
  | MessageBodyAttachment
  | MessageBodySurvey
  | MessageBodyResource;

interface BaseUniversalChatMessage {
  id: string | null;
  roomId: string;
  encounterId: string | null;
  clientId: string | null;
  senderId: string | null;
  senderType: string;
  metadata?: {
    tags?: string[];
  };
  createdAt: string;
  updatedAt: string;
  visibilityRestrictedTo?: MessageVisibilityRestriction | null;
  processingStatus?: MessageProcessingStatus | null;
}

export interface TextUniversalChatMessage extends BaseUniversalChatMessage {
  type: UniversalMessageType.TEXT | "text";
  body: MessageBodyText;
}

export interface AttachmentUniversalChatMessage
  extends BaseUniversalChatMessage {
  type: UniversalMessageType.ATTACHMENT | "attachment";
  body: MessageBodyAttachment;
}

export interface SurveyUniversalChatMessage extends BaseUniversalChatMessage {
  type: UniversalMessageType.SURVEY | "survey";
  body: MessageBodySurvey;
}

export interface ResourceUniversalChatMessage extends BaseUniversalChatMessage {
  type: UniversalMessageType.RESOURCE | "resource";
  body: MessageBodyResource;
}

/**
 * Discriminated union ensures type safety when accessing body fields based on the message type.
 */
export type UniversalChatMessage =
  | TextUniversalChatMessage
  | AttachmentUniversalChatMessage
  | SurveyUniversalChatMessage
  | ResourceUniversalChatMessage;

export interface UniversalChatRoom {
  id: string;
  ownerId: string;
  ownerType: string;
  type: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatParticipant {
  id: string | null;
  type: string;
  displayName: string;
  photoUrl: string | null;
}

export interface UniversalChatPage {
  data: {
    messages: UniversalChatMessage[];
    room?: UniversalChatRoom;
    participants?: ChatParticipant[];
  };
}

export interface UniversalChatQueryData {
  pages: UniversalChatPage[];
  messages: UniversalChatMessage[];
  room?: UniversalChatRoom;
  participants?: ChatParticipant[];
}

interface BaseWebSocketMessageData {
  id?: string | null;
  roomId?: string;
  encounterId?: string | null;
  clientId?: string | null;
  senderId?: string | null;
  senderType: string;
  createdAt: string;
  updatedAt?: string;
  socketEventType?: string;
  participant?: ChatParticipant;
  processingStatus?: MessageProcessingStatus | null;
  metadata?: {
    tags?: string[];
  };
  visibilityRestrictedTo?: MessageVisibilityRestriction | null;
}

export interface TextWebSocketMessageData extends BaseWebSocketMessageData {
  type: UniversalMessageType.TEXT | "text";
  body: MessageBodyText;
}

export interface AttachmentWebSocketMessageData
  extends BaseWebSocketMessageData {
  type: UniversalMessageType.ATTACHMENT | "attachment";
  body: MessageBodyAttachment;
}

export interface SurveyWebSocketMessageData extends BaseWebSocketMessageData {
  type: UniversalMessageType.SURVEY | "survey";
  body: MessageBodySurvey;
}

export interface ResourceWebSocketMessageData extends BaseWebSocketMessageData {
  type: UniversalMessageType.RESOURCE | "resource";
  body: MessageBodyResource;
}

/**
 * Discriminated union ensures type safety when accessing body fields based on the message type.
 */
export type WebSocketMessageData =
  | TextWebSocketMessageData
  | AttachmentWebSocketMessageData
  | SurveyWebSocketMessageData
  | ResourceWebSocketMessageData;

export interface OutgoingChatMessage {
  encounterId: string;
  roomId: string;
  clientId: string;
  senderId: string;
  senderType: string;
  type: string;
  body: {
    text: string;
    markdown: boolean;
  };
  createdAt: string;
}

export interface UpdateRoomMetadataMessage {
  socketEventType: "update_room_metadata";
  openEncounterId: string;
  customerWaitingSince: string | null;
  roomId: string;
  patientId: string;
}
